import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import clsx from 'clsx';
import { useMediaQuery, Grid, Typography, Button } from '@material-ui/core';

// UTILITY
import { getS3ImageUrl } from '@/utility/content';

// COMPONENTS
import Image from '@/components/Image';

// STYLES
import { Container } from './styles';

const ContentSections = ({ variant, imageName, title, subtitle, description, ButtonProps }) => {
  const downMd = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const imagePath = React.useMemo(
    () => getS3ImageUrl(`/${imageName}-${downMd ? 'mobile' : 'desktop'}`),
    [imageName, downMd],
  );
  const buttonColor = React.useMemo(
    () => (variant === 'blue' || variant === 'purple' ? 'default' : 'primary'),
    [variant],
  );

  const handleClick = React.useCallback(
    e => {
      e.stopPropagation();
      if (ButtonProps.onClick) ButtonProps.onClick();
    },
    [ButtonProps.onClick],
  );

  return (
    <Container className={clsx('content-section', variant && `content-section--${variant}`)}>
      <div className="content-section__wrapper">
        <div
          className={`page-content content-section__page-content content-section__page-content--${imageName}`}
        >
          <Grid container spacing={downMd ? 5 : 2}>
            <Grid item xs={12} md={6}>
              <div className="content-section__left">
                <Typography
                  className="content-section__subtitle subtitle-secondary"
                  variant="inherit"
                  color="inherit"
                  component="p"
                  align={downMd ? 'center' : 'left'}
                >
                  {subtitle}
                </Typography>
                <Typography
                  className={clsx('content-section__title')}
                  variant="h2"
                  color="inherit"
                  align="left"
                >
                  {title}
                </Typography>
                <Trans
                  components={{
                    p: (
                      <Typography
                        className="p3 content-section__description"
                        variant="inherit"
                        color="inherit"
                        component="p"
                        align="left"
                      />
                    ),
                    ul: (
                      <ul className="pros-cons-list pros-cons-list--square-icons pros-cons-list--with-icons content-section__description" />
                    ),
                    li: <li className="pcl-item" />,
                  }}
                >
                  {description}
                </Trans>
                <Button
                  color={buttonColor}
                  className="content-section__button"
                  fullWidth={downMd}
                  {...ButtonProps}
                  onClick={handleClick}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={imageName}>
              <Image
                className="content-section__image"
                src={`${imagePath}.png`}
                srcSet={`${imagePath}.webp`}
                alt={imageName}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </Container>
  );
};

ContentSections.defaultProps = {
  variant: 'white',
  title: '',
  subtitle: '',
  description: '',
  ButtonProps: {},
};

ContentSections.propTypes = {
  // content-thumbnails | channels-with-gradient | channels-with-circles
  imageName: PropTypes.string.isRequired,
  variant: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  ButtonProps: PropTypes.object,
};

export default ContentSections;
